import { Card, Group, Skeleton, Flex } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useCallback, useState } from "react";
import RefreshQueryButton from "@/components/refresh-query-button";
import { DESAFIOS_COLLECTION_KEY } from "@/modules/desafios/firestore";
import ExportExcel from "@/modules/desafios-relatorio-visao-geral/components/ExportExcel";
import FiltrosAtivos from "@/modules/desafios-relatorio-visao-geral/components/FiltrosAtivos";
import ModalFilterDesafios, {
  FiltroDesafio
} from "@/modules/desafios-relatorio-visao-geral/components/ModalFilterDesafios";
import RelatorioVisaoGeralCard from "@/modules/desafios-relatorio-visao-geral/components/RelatorioVisaoGeralCard";
import useUserStore from "@/modules/users/store";
import RelatorioVisaoGeralDesafiosTable from "@/src/modules/desafios-relatorio-visao-geral/components/RelatorioVisaoGeralDesafiosTable";
import { useDesafiosRelatorio } from "../../modules/desafios-relatorio-visao-geral/hooks/useDesafiosRelatorio";

const DIAS_MES = 30;
const REFRESH_ALL_KEY = [DESAFIOS_COLLECTION_KEY];
const HORA_INICIO = 0;
const MINUTO_INICIO = 0;
const SEGUNDO_INICIO = 0;
const MILISSEGUNDO_INICIO = 0;
const HORA_FIM = 23;
const MINUTO_FIM = 59;
const SEGUNDO_FIM = 59;
const MILISSEGUNDO_FIM = 999;

interface DateFormat {
  dataInicial: Date;
  dataFinal: Date;
}

export interface Cards {
  colaboradoresAtivos: number;
  desafios: {
    total: number;
    participantes: Set<string>;
    porcentagem: number;
  };
  comentarios: {
    total: number;
    participantes: Set<string>;
    porcentagem: number;
  };
}

const mockFilterDesafios: FiltroDesafio = {
  search: "",
  campo: "",
  status: ""
};

// prettier-ignore
const RelatorioVisaoGeralDesafiosPage = () => {
  const [date, setDate] = useState<DateFormat>(() => {
    const dataInicial = new Date(new Date().setDate(new Date().getDate() - DIAS_MES));
    dataInicial.setHours(HORA_INICIO, MINUTO_INICIO, SEGUNDO_INICIO, MILISSEGUNDO_INICIO);
    
    const dataFinal = new Date();
    dataFinal.setHours(HORA_FIM, MINUTO_FIM, SEGUNDO_FIM, MILISSEGUNDO_FIM);
    
    return { dataInicial, dataFinal };
  });
  const [datePickerValue, setDatePickerValue] = useState<[Date | null, Date | null]>([
    new Date(new Date().setDate(new Date().getDate() - DIAS_MES)),
    new Date()
  ]);
  const empresa = useUserStore((state) => state.activeEmpresa);
  const [filterDesafios, setFilterDesafios] = useState<FiltroDesafio>(mockFilterDesafios);

  const { cards, desafiosProcessados, isLoading } = useDesafiosRelatorio(date, filterDesafios);

  const onDateIniciaFinalChange = useCallback((dates: [Date | null, Date | null]) => {
    setDatePickerValue(dates);
    const [dataInicial, dataFinal] = dates;
    if (dataInicial && dataFinal) {
      // Ajustar data inicial para 00:00:00
      const dataInicialAjustada = new Date(dataInicial);
      dataInicialAjustada.setHours(HORA_INICIO, MINUTO_INICIO, SEGUNDO_INICIO, MILISSEGUNDO_INICIO);
      
      // Ajustar data final para 23:59:59
      const dataFinalAjustada = new Date(dataFinal);
      dataFinalAjustada.setHours(HORA_FIM, MINUTO_FIM, SEGUNDO_FIM, MILISSEGUNDO_FIM);
      
      setDate({ 
        dataInicial: dataInicialAjustada, 
        dataFinal: dataFinalAjustada 
      });
    }
  }, []);

  return (
    <Card withBorder shadow="md">
      <Card.Section inheritPadding py="md" withBorder>
        <Flex justify="space-between">
          <DatePickerInput
            type="range"
            w="340px"
            valueFormat="DD [de] MMMM, YYYY"
            value={datePickerValue}
            onChange={onDateIniciaFinalChange}
            label="Período"
            maxDate={new Date()}
          />
          <ExportExcel
            cards={cards}
            empresa={empresa}
            dadosFiltrados={desafiosProcessados}
            isLoading={isLoading}
          />
        </Flex>
      </Card.Section>
      <Card.Section inheritPadding py="md" withBorder>
        <Group justify="space-between">
          <Group>
            <ModalFilterDesafios setFilter={setFilterDesafios} />
            <FiltrosAtivos
              filtros={filterDesafios}
              totalResultados={desafiosProcessados.length}
            />
          </Group>
          <RefreshQueryButton queryKey={REFRESH_ALL_KEY} />
        </Group>
      </Card.Section>
      {isLoading ? (
        <Group justify="center" mt="md" mb="md" gap="xs">
          <Skeleton height={300} width={200} />
          <Skeleton height={300} width={200} />
          <Skeleton height={300} width={200} />
        </Group>
      ) : (
        <Group justify="center" mt="md" mb="md" gap="xs">
          <RelatorioVisaoGeralCard cards={cards} tipo="desafios" />
          <RelatorioVisaoGeralCard cards={cards} tipo="comentarios" />
          <RelatorioVisaoGeralCard cards={cards} tipo="resumo" />
        </Group>
      )}
      <RelatorioVisaoGeralDesafiosTable
        data={desafiosProcessados}
        isLoading={isLoading}
      />
    </Card>
  );
};

export default RelatorioVisaoGeralDesafiosPage;
